import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { addSongToLibrary, removeSongFromLibrary } from "../../actions/libraryActions";
import { bookmarkSong, createSet } from "../../actions/setsActions";
import { requestSong, getRequestName } from "../../actions/requestsActions";
import { selectIsHost, selectIsInLibrary, selectLastUsedSet, selectSets } from "../../selectors";
import Popup from "./Popup";

const formatDate = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString([], { month: "short", day: "numeric" });
};

/** "Artist · 5 sections · private", whichever parts the song has. */
export const songMeta = (song) => {
  const parts = [];
  if (song.SungAt) parts.push(`Sung ${formatDate(song.SungAt)}${song.SungIn ? ` · ${song.SungIn}` : ""}`);
  if (song.Artist) parts.push(song.Artist);
  if (song.Verses?.length) parts.push(`${song.Verses.length} ${song.Verses.length === 1 ? "section" : "sections"}`);
  if (song.Key) parts.push(`Key ${song.Key}`);
  return parts.join(" · ");
};

/** Asks a guest who is requesting for their name (kept on the device) before the request goes out. */
const RequestDialog = ({ song, onClose }) => {
  const dispatch = useDispatch();
  const code = useSelector((state) => state.session.Code);
  const [name, setName] = useState(getRequestName);
  const [sending, setSending] = useState(false);

  const send = async (event) => {
    event.preventDefault();
    setSending(true);
    await dispatch(requestSong(code, song, name));
    onClose();
  };

  return (
    <Popup onClose={onClose} title="Request a song">
      <form className="stack" onSubmit={send}>
        <div className="row-title">{song.Title}</div>
        <p className="note">It lands in the host’s set list as a one-tap decision. Your name is optional.</p>
        <input className="field" value={name} onChange={(event) => setName(event.target.value)} placeholder="Your name (optional)" maxLength={50} autoComplete="name" aria-label="Your name" />
        <button type="submit" className="btn btn-md btn-primary btn-block" disabled={sending}>
          {sending ? "Sending…" : "Send the request"}
        </button>
      </form>
    </Popup>
  );
};

/**
 * A song in the Songs and Favorites lists: tap to open it; hosts mark favourites and
 * bookmark it into a set (one tap for the last set used, the caret picks another); guests
 * can request it.
 */
const SongRow = ({ song, meta, showRequest = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isHost = useSelector(selectIsHost);
  const hasKey = useSelector((state) => state.host.hasKey);
  const inLibrary = useSelector((state) => selectIsInLibrary(state, song.Id));
  const sets = useSelector(selectSets);
  const lastSet = useSelector(selectLastUsedSet);
  const [picking, setPicking] = useState(false);
  const [requesting, setRequesting] = useState(false);

  const toggleLibrary = () => dispatch(inLibrary ? removeSongFromLibrary(song.Id) : addSongToLibrary(song.Id));

  const bookmark = () => {
    if (lastSet) dispatch(bookmarkSong(lastSet.Id, song));
    else setPicking(true);
  };

  const pick = (setId) => {
    setPicking(false);
    dispatch(bookmarkSong(setId, song));
  };

  const newSet = async () => {
    const name = window.prompt("Name for the new set", "");
    if (!name || !name.trim()) return;
    setPicking(false);
    const set = await dispatch(createSet({ name: name.trim(), songIds: [song.Id] }));
    if (set) dispatch(bookmarkSong(set.Id, song));
  };

  return (
    <li className="stack stack-tight">
      <div className={`row${picking ? " row-picking" : ""}`}>
        <button type="button" className="row-main" onClick={() => navigate(`/songs/${song.Id}`)}>
          <div className="row-title">
            {song.Title}
            {song.OwnerId != null && (
              <span className="private-mark" title="Only its owner finds this song in the lists">
                private
              </span>
            )}
          </div>
          <div className="row-sub">{meta ?? songMeta(song)}</div>
        </button>
        {isHost && (
          <button
            type="button"
            className="icon-btn"
            onClick={toggleLibrary}
            aria-label={inLibrary ? "Remove from favourites" : "Add to favourites"}
            aria-pressed={inLibrary}
          >
            {inLibrary ? "♥" : "♡"}
          </button>
        )}
        {isHost && hasKey && (
          <span className="split-btn">
            <button type="button" onClick={bookmark} aria-label={lastSet ? `Add to ${lastSet.Name}` : "Add to a set"}>
              ⌖
            </button>
            <span aria-hidden="true" />
            <button type="button" onClick={() => setPicking((open) => !open)} aria-label="Pick a set" aria-expanded={picking}>
              ▾
            </button>
          </span>
        )}
        {showRequest && !isHost && (
          <button type="button" className="btn btn-xs btn-primary" onClick={() => setRequesting(true)}>
            Request
          </button>
        )}
      </div>
      {picking && (
        <div className="picker" role="group" aria-label="Add to">
          <div className="kicker">Add to</div>
          {sets.map((set) => {
            const inSet = set.Songs.some((item) => item.Id === song.Id);
            return (
              <button key={set.Id} type="button" className="picker-item" onClick={() => pick(set.Id)}>
                <span className="swatch" aria-hidden="true" />
                <span className="grow">{set.Name}</span>
                <small>{inSet ? "in it" : lastSet?.Id === set.Id ? "· last used" : ""}</small>
              </button>
            );
          })}
          <button type="button" className="picker-item muted" onClick={newSet}>
            <span className="swatch swatch-new" aria-hidden="true" />
            <span className="grow">＋ New set</span>
          </button>
        </div>
      )}
      {requesting && <RequestDialog song={song} onClose={() => setRequesting(false)} />}
    </li>
  );
};

export default SongRow;
